
import { FileText, Upload, CheckCircle, AlertCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { ProductDescription } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { toast } from "sonner";

interface DashboardStatsProps {
  descriptions?: ProductDescription[];
}

export const DashboardStats = ({ descriptions }: DashboardStatsProps) => {
  const { organization } = useAuth();

  const { data: subscription } = useQuery({
    queryKey: ["organizationSubscription", organization?.id],
    queryFn: async () => {
      console.log("Fetching subscription stats for org:", organization?.id);
      try {
        // If user is part of an organization, get organization subscription
        if (organization?.id) {
          const { data, error } = await supabase
            .from("organization_subscriptions")
            .select(`
              products_used,
              start_date,
              subscription_plans(
                product_limit
              )
            `)
            .eq("organization_id", organization.id)
            .maybeSingle();

          if (error) {
            console.error('Error fetching organization subscription:', error);
            throw error;
          }

          console.log("Organization subscription data:", data);
          return data;
        }

        // Otherwise get user's personal subscription
        const { data, error } = await supabase
          .from("user_subscriptions")
          .select(`
            products_used,
            start_date,
            subscription_plans(
              product_limit
            )
          `)
          .is("organization_id", null)
          .maybeSingle();

        if (error) {
          console.error('Error fetching user subscription:', error);
          throw error;
        }

        console.log("User subscription data:", data);
        return data;
      } catch (err) {
        console.error("Failed to fetch subscription:", err);
        toast.error("Could not load subscription information");
        return null;
      }
    },
    retryDelay: attempt => Math.min(attempt * 1000, 30000),
    retry: 2,
  });

  const productLimit = subscription?.subscription_plans?.[0]?.product_limit || 0;
  const productsUsed = subscription?.products_used || 0;
  const remainingProducts = productLimit - productsUsed;

  const stats = [
    {
      label: (
        <div className="space-y-1">
          <p>Total Descriptions Generated</p>
          <p className="text-xs text-muted-foreground">
            Products used: {productsUsed}
            {productLimit > 0 && 
              ` / ${productLimit} (${remainingProducts} remaining)`
            }
          </p>
        </div>
      ),
      value: descriptions?.length || "0",
      icon: FileText,
      color: "text-primary",
    },
    {
      label: "Pending Approval",
      value: descriptions?.filter(d => d.status === 'pending').length || "0",
      icon: Upload,
      color: "text-yellow-500",
    },
    {
      label: "Approved",
      value: descriptions?.filter(d => d.status === 'approved').length || "0",
      icon: CheckCircle,
      color: "text-green-500",
    },
    {
      label: "Failed",
      value: descriptions?.filter(d => d.status === 'error' || d.processing_status === 'error').length || "0",
      icon: AlertCircle,
      color: "text-red-500",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {stats.map((stat) => (
        <Card key={typeof stat.label === 'string' ? stat.label : 'Total Descriptions Generated'} className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-sm font-medium text-muted-foreground">
                {stat.label}
              </div>
              <p className="text-2xl font-bold mt-2">{stat.value}</p>
            </div>
            <stat.icon className={`w-6 h-6 ${stat.color}`} /> {/* Increased from w-5 h-5 to w-6 h-6 */}
          </div>
        </Card>
      ))}
    </div>
  );
};
